import { useCallback } from 'react';
import { TableCellTypes, Button, ButtonVariants, ButtonSizes } from '@carboledger/ui';
import {
  makeUnitTypeCell,
  makeUnitScopedCell,
  makeAssignQuantityCell,
  makeDiscrepanciesCell,
} from '@/components/common';
import { buildRowUnitOptions, unitTypeLabel } from './allocationUnits';

// Columns for the Link existing shipment wizard, in Notion's order. Nothing sorts.

const renderUnitTypeCell = makeUnitTypeCell((t, row) => buildRowUnitOptions(t, row.byUnit));
const renderUnassignedQuantity = makeUnitScopedCell('unassignedQuantity');
const renderShipmentQuantity = makeUnitScopedCell('shipmentQuantity');
const renderRemainingToAllocate = makeUnitScopedCell('remainingToAllocateQuantity');

const textColumn = (id, header, width = 180) => ({
  id,
  header,
  type: TableCellTypes.text,
  width,
});

const unitColumn = (id, header, cell, headerSelect) => ({
  id,
  header,
  type: TableCellTypes.custom,
  width: 200,
  cell,
  headerSelect,
});

// The sustainability columns both steps repeat, after the quantities.
const sustainabilityColumns = (t) => [
  textColumn('ghgEmissions', t('FIELD_GHG_EMISSIONS')),
  textColumn('ghgEmissionSavings', t('LINK_SHIPMENTS_COL_GHG_SAVINGS'), 150),
  textColumn('rawMaterialType', t('FIELD_RAW_MATERIAL_TYPE')),
  textColumn('countryOfOrigin', t('FIELD_COUNTRY_OF_ORIGIN')),
  textColumn('feedstockCategory', t('FIELD_FEEDSTOCK_CATEGORY')),
  textColumn('allocationPreference', t('FIELD_ALLOCATION_PREFERENCE')),
  textColumn('dateOfDispatch', t('FIELD_DATE_OF_DISPATCH'), 150),
];

const quantityColumns = (t, headerSelect) => [
  {
    id: 'unitType',
    header: t('FIELD_UNIT_TYPE', { unit: unitTypeLabel(t, headerSelect.value) }),
    type: TableCellTypes.custom,
    width: 220,
    cell: renderUnitTypeCell,
    headerSelect,
  },
  unitColumn('unassignedQuantity', t('FIELD_UNASSIGNED_QUANTITY'), renderUnassignedQuantity),
  unitColumn('shipmentQuantity', t('FIELD_SHIPMENT_QUANTITY'), renderShipmentQuantity),
  unitColumn(
    'remainingToAllocateQuantity',
    t('ORDERS_COL_REMAINING_TO_ALLOCATE'),
    renderRemainingToAllocate,
  ),
];

/** Step 1 — every outgoing shipment the allocation could take, ticked with a checkbox. */
export const buildAllocationLinkStep1Columns = (t, { headerSelect, onOpenDiscrepancy }) => [
  textColumn('shipmentId', t('FIELD_SHIPMENT_ID'), 160),
  textColumn('totalOrdersLinked', t('ALLOCATIONS_COL_TOTAL_ORDERS_LINKED'), 160),
  ...quantityColumns(t, headerSelect),
  ...sustainabilityColumns(t),
  {
    id: 'discrepancies',
    header: t('FIELD_DISCREPANCIES'),
    type: TableCellTypes.custom,
    width: 200,
    cell: makeDiscrepanciesCell(onOpenDiscrepancy),
  },
];

/**
 * Props:
 *   row       the shipment being dropped from step 2
 *   onRemove  (rowId) => void
 *   label     the button's text
 */
const RemoveCell = ({ row, onRemove, label }) => {
  const handleClick = useCallback(() => onRemove(row.id), [onRemove, row.id]);

  return (
    <Button variant={ButtonVariants.tertiary} size={ButtonSizes.sm} onClick={handleClick}>
      {label}
    </Button>
  );
};

/** Step 2 — the picked shipments, each taking a quantity, each droppable. */
export const buildAllocationLinkStep2Columns = (
  t,
  { headerSelect, assignments, onAssign, onRemove },
) => [
  textColumn('shipmentId', t('FIELD_SHIPMENT_ID'), 160),
  {
    id: 'assignQuantity',
    header: t('LINK_ASSIGN_QUANTITY'),
    type: TableCellTypes.custom,
    width: 220,
    cell: makeAssignQuantityCell(assignments, onAssign),
  },
  textColumn('totalOrdersLinked', t('ALLOCATIONS_COL_TOTAL_ORDERS_LINKED'), 160),
  ...quantityColumns(t, headerSelect),
  ...sustainabilityColumns(t),
  {
    id: '__remove__',
    header: '',
    type: TableCellTypes.custom,
    width: 100,
    cell: (ctx) => <RemoveCell row={ctx.row} onRemove={onRemove} label={t('COMMON_REMOVE')} />,
  },
];
